'use client'

import { ColumnDef } from "@tanstack/react-table"
import { ArrowUpDown, ExternalLink, FileText, Hash, Tag, Trash2 } from "lucide-react"
import { Button } from "@/components/ui/button"
import { Badge } from '@/components/ui/badge'
import { useToast } from "@/hooks/use-toast"
import ChartOfAccountsService from '@/api/chartofaccounts.service'
import { useState } from "react"
import { AccountType } from "@/types/index"
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog"

export const columns: ColumnDef<any>[] = [
  {
    accessorKey: "code",
    header: ({ column }) => (
      <Button
        variant="ghost"
        onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}
        className="font-medium text-slate-800 dark:text-slate-200 hover:text-slate-900 dark:hover:text-white whitespace-nowrap"
      >
        Account Code
        <ArrowUpDown className="w-4 h-4 ml-2 opacity-70" />
      </Button>
    ),
    cell: ({ cell }) => (
      <div className="flex items-center">
        <Hash className="w-4 h-4 mr-2 text-slate-400" />
        <span className="font-mono text-sm font-medium text-slate-900 dark:text-slate-100">
          {cell.getValue() as string}
        </span>
      </div>
    ),
  },
  {
    accessorKey: "name",
    header: ({ column }) => (
      <Button
        variant="ghost"
        onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}
        className="font-medium text-slate-800 dark:text-slate-200 hover:text-slate-900 dark:hover:text-white whitespace-nowrap"
      >
        Account Name
        <ArrowUpDown className="w-4 h-4 ml-2 opacity-70" />
      </Button>
    ),
    cell: ({ cell }) => (
      <div className="font-medium text-slate-900 dark:text-slate-100">
        {cell.getValue() as string}
      </div>
    ),
  },
  {
    accessorKey: "type",
    header: ({ column }) => (
      <Button
        variant="ghost"
        onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}
        className="font-medium text-slate-800 dark:text-slate-200 hover:text-slate-900 dark:hover:text-white"
      >
        Type
        <ArrowUpDown className="w-4 h-4 ml-2 opacity-70" />
      </Button>
    ),
    cell: ({ cell }) => {
      const type = cell.getValue() as AccountType;
      const badgeConfig: Record<AccountType, { color: string, icon: string }> = {
        'ASSET': {
          color: 'bg-emerald-50 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-400 border-emerald-200 dark:border-emerald-800',
          icon: '💰'
        },
        'LIABILITY': {
          color: 'bg-red-50 text-red-700 dark:bg-red-900/30 dark:text-red-400 border-red-200 dark:border-red-800',
          icon: '🧾'
        }, 
        'EQUITY': {
          color: 'bg-indigo-50 text-indigo-700 dark:bg-indigo-900/30 dark:text-indigo-400 border-indigo-200 dark:border-indigo-800',
          icon: '🏦'
        },
        'INCOME': {
          color: 'bg-blue-50 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400 border-blue-200 dark:border-blue-800',
          icon: '💸'
        },
        'EXPENSE': {
          color: 'bg-amber-50 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400 border-amber-200 dark:border-amber-800',
          icon: '💳'
        }
      };
      
      return (
        <Badge variant="outline" className={`font-medium flex items-center gap-1.5 px-2.5 py-1 rounded-full ${badgeConfig[type].color}`}>
          <span>{badgeConfig[type].icon}</span>
          <span>{type}</span>
        </Badge>
      );
    },
  },
  {
    accessorKey: "subtype",
    header: ({ column }) => (
      <Button
        variant="ghost"
        onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}
        className="font-medium text-slate-800 dark:text-slate-200 hover:text-slate-900 dark:hover:text-white"
      >
        Subtype
        <ArrowUpDown className="w-4 h-4 ml-2 opacity-70" />
      </Button>
    ),
    cell: ({ cell }) => {
      const subtype = cell.getValue() as string;
      if (!subtype) return <span className="text-slate-400">-</span>;
      
      return (
        <div className="flex items-center">
          <Tag className="w-3.5 h-3.5 mr-2 text-slate-400" />
          <span className="text-sm text-slate-700 dark:text-slate-300">{subtype}</span>
        </div>
      );
    },
  },
  {
    accessorKey: "isActive",
    header: "Status",
    cell: ({ cell }) => {
      const isActive = cell.getValue() as boolean;
      
      return (
        <Badge 
          variant="outline" 
          className={isActive 
            ? "bg-green-50 text-green-700 dark:bg-green-900/30 dark:text-green-400 border-green-200 dark:border-green-800"
            : "bg-slate-100 text-slate-700 dark:bg-slate-800 dark:text-slate-400 border-slate-200 dark:border-slate-700"
          }
        >
          {isActive ? "Active" : "Inactive"}
        </Badge>
      );
    },
  },
  {
    accessorKey: "defaultAccount",
    header: "Default",
    cell: ({ cell }) => {
      const isDefault = cell.getValue() as boolean;
      
      if (!isDefault) return <span className="text-slate-400">-</span>;
      
      return (
        <Badge variant="outline" className="bg-blue-50 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400 border-blue-200 dark:border-blue-800">
          Default
        </Badge>
      );
    },
  },
  {
    id: "actions",
    header: "Actions",
    cell: ({ row }) => {
      const { toast } = useToast();
      const [showDeleteDialog, setShowDeleteDialog] = useState(false);
      
      const handleDelete = async () => {
        try {
          const response = await ChartOfAccountsService.deleteChartOfAccount(row.original.publicId);
          if (response.data.result === 'success') {
            toast({
              title: "Chart of Account Deleted",
              description: "Chart of account has been successfully deleted",
              variant: "default",
            });
            setTimeout(() => {
              window.location.reload();
            }, 1000);
          } else {
            toast({
              title: "Error",
              description: response.data.message,
              variant: "destructive",
            });
          }
        } catch (error) {
          toast({
            title: "Error",
            description: "Failed to delete chart of account",
            variant: "destructive",
          });
        } finally {
          setShowDeleteDialog(false);
        }
      };

      return (
        <>
          <div className="flex items-center gap-2">
            <Button 
              onClick={() => window.location.href = `/chart-of-accounts/${row.original.publicId}`} 
              variant="outline"
              className="h-8 px-3 text-slate-700 dark:text-slate-200 border-slate-200 dark:border-slate-700 hover:bg-slate-100 dark:hover:bg-slate-800"
            >
              <ExternalLink className="w-3.5 h-3.5 mr-2" />
              View
            </Button>
            <Button 
              onClick={() => setShowDeleteDialog(true)} 
              variant="outline"
              className="h-8 px-3 text-rose-600 dark:text-rose-500 border-rose-200 dark:border-rose-800 hover:bg-rose-50 dark:hover:bg-rose-900/30"
              disabled={row.original.defaultAccount}
              title={row.original.defaultAccount ? "Default accounts cannot be deleted" : "Delete account"}
            >
              <Trash2 className="w-3.5 h-3.5 mr-2" />
              Delete
            </Button>
          </div>
          
          <AlertDialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
            <AlertDialogContent>
              <AlertDialogHeader>
                <AlertDialogTitle>Are you sure?</AlertDialogTitle>
                <AlertDialogDescription>
                  This will permanently delete the "{row.original.name}" account. This action cannot be undone.
                </AlertDialogDescription>
              </AlertDialogHeader>
              <AlertDialogFooter>
                <AlertDialogCancel>Cancel</AlertDialogCancel>
                <AlertDialogAction onClick={handleDelete} className="bg-rose-600 hover:bg-rose-700 text-white">
                  Delete
                </AlertDialogAction>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialog>
        </>
      );
    }
  }
] 